
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Rental } from '@/services/types';
import { StatusBadge } from '@/components/StatusBadge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { mockRentals } from '@/services/mockData';
import { fetchTableData } from '@/integrations/supabase/tableUtils';
import { Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface RentalHistoryProps {
  courierId: string;
}

export function RentalHistoryTab({ courierId }: RentalHistoryProps) {
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRentals = async () => {
      try {
        setLoading(true);
        console.log(`Получение истории аренд для курьера ${courierId}...`);
        
        // Получаем сначала список аренд (без JOIN)
        const { data: rentalData, error: rentalError } = await fetchTableData(
          `rentals`,
          { 
            select: '*',
            filter: `courier_id=eq.${courierId}`
          }
        );
        
        if (rentalError || !rentalData) {
          console.error('Ошибка при загрузке аренд или данные пусты:', rentalError);
          const courierRentals = mockRentals.filter(rental => rental.courier_id === courierId);
          setRentals(courierRentals);
          setError('Произошла ошибка при загрузке данных');
        } else {
          console.log('Получены данные аренд:', rentalData);
          
          // Создаем массив для хранения результатов с информацией о велосипедах
          let courierRentals: Rental[] = [];
          
          // Для каждой аренды получаем информацию о велосипеде
          for (const rental of rentalData) {
            try {
              // Получаем информацию о велосипеде
              const { data: bikeData, error: bikeError } = await fetchTableData(
                `bikes`, 
                { 
                  select: 'id,model,gps_tag',
                  filter: `id=eq.${rental.bike_id}`
                }
              );
              
              const bikeInfo = bikeData && bikeData.length > 0 ? bikeData[0] : null;
              const bikeNameValue = bikeInfo 
                ? `${bikeInfo.model || 'Не указано'} ${bikeInfo.gps_tag ? `(${bikeInfo.gps_tag})` : ''}`
                : 'Не указано';
              
              courierRentals.push({
                id: rental.id,
                bikeName: bikeNameValue,
                courierName: '',
                startDate: rental.start_date,
                endDate: rental.end_date,
                status: rental.status || 'active',
                totalAmount: rental.amount || 0,
                human_number: rental.human_number,
                courier_id: rental.courier_id,
                bike_id: rental.bike_id,
                start_date: rental.start_date,
                end_date: rental.end_date,
                // Используем только прямую ссылку из поля documents_url
                documentsUrl: rental.documents_url,
              });
            } catch (bikeError) {
              console.error(`Ошибка при получении данных о велосипеде ${rental.bike_id}:`, bikeError);
              
              // Добавляем запись даже если не удалось получить данные о велосипеде
              courierRentals.push({
                id: rental.id,
                bikeName: 'Не указано',
                courierName: '',
                startDate: rental.start_date,
                endDate: rental.end_date,
                status: rental.status || 'active',
                totalAmount: rental.amount || 0,
                human_number: rental.human_number,
                courier_id: rental.courier_id,
                bike_id: rental.bike_id,
                start_date: rental.start_date,
                end_date: rental.end_date,
                // Используем только прямую ссылку из поля documents_url
                documentsUrl: rental.documents_url,
              });
            }
          }
          
          setRentals(courierRentals);
          console.log(`Загружено ${courierRentals.length} записей аренд для курьера`);
        }
      } catch (err) {
        console.error('Ошибка при загрузке истории аренд:', err);
        const fallbackRentals = mockRentals.filter(rental => rental.courier_id === courierId);
        setRentals(fallbackRentals);
        setError('Произошла ошибка при загрузке данных');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRentals();
  }, [courierId]);

  // Сортируем аренды по дате, от новых к старым
  const sortedRentals = [...rentals].sort((a, b) => 
    new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy', { locale: ru });
    } catch (error) {
      console.error('Ошибка форматирования даты:', error, dateString);
      return 'Некорректная дата';
    }
  };

  const handleOpenDocument = (documentsUrl: string | undefined) => {
    if (!documentsUrl) {
      return;
    }
    window.open(documentsUrl, '_blank');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>История аренд</CardTitle>
        <CardDescription>
          Все аренды оформленные клиентом
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Загрузка данных...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {rentals.length > 0 ? (
              rentals.map((rental) => {
                // Добавляем отладочные логи
                console.log(`Аренда ${rental.id}:`, {
                  documentsUrl: rental.documentsUrl,
                  hasDocumentsUrl: !!rental.documentsUrl
                });
                
                return (
                  <div key={rental.id} className="flex items-center justify-between border-b pb-2">
                    <div>
                      <div className="font-medium">
                        {rental.bikeName || `Велосипед ${String(rental.id).substring(0, 8)}`} 
                        {rental.human_number ? (
                          <span className="ml-1 text-xs text-primary-foreground bg-primary px-2 py-0.5 rounded">
                            {rental.human_number}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground ml-1">
                            #{String(rental.id).substring(0, 8)}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(rental.startDate)} 
                        {rental.endDate && ` - ${formatDate(rental.endDate)}`}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="font-medium">{rental.totalAmount || rental.amount || 0} ₽</div>
                        <StatusBadge status={rental.status} type="rental" />
                      </div>
                      {rental.documentsUrl && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDocument(rental.documentsUrl)}
                          title="Открыть документ"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                      {/* Временно показываем состояние для отладки */}
                      <div className="text-xs text-gray-400">
                        Doc: {rental.documentsUrl ? '✓' : '✗'}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-muted-foreground">
                {error ? `${error}. Используются тестовые данные.` : 'Нет данных об арендах'}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
